import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import BreadCrumb from 'jbc-front/components/BreadCrumb';
import Button from 'jbc-front/components/Button';
import { Section, TextField, RadioField, SelectField, CheckboxField, Label } from 'src/jbc-front/components/Form';
import formStyles from 'jbc-front/components/Form.scss';
import ErrorsSection from 'src/components/ErrorsSection';

import { getMonthOptions, getQuarterOptions } from 'src/utils/Utils';
import { REPORT_INDEX, INCOME_TAX_DECLARATION_LIST } from 'src/constants/EndpointUrls';
import YearPickerField from './YearPickerField';
import CreateDeclarationLoadingModal from './CreateDeclarationLoadingModal';
import ExistingDeclarationModal from './ExistingDeclarationModal';
import UnconfirmedPayrollModal from './UnconfirmedPayrollModal';

import './CreateDeclarationForm.scss';

const CreateDeclarationForm = ({
  handleSubmit,
  submitting,
  error,
  periodType,
  year,
  periodMonth,
  periodQuarter,
  formCode,
  branchOptions,
  selectedBranch,
  periodTypes,
  canIncludeAppendix,
  isCreating,
  formErrors,
  isExistingModalOpen,
  isUnconfirmedModalOpen,
  unconfirmedPaymentMonths,
  onCancel,
  onChangeDeclarationName,
  onExistingModalCancel,
  onExistingModalConfirm,
  onUnconfirmedModalCancel,
  onUnconfirmedModalConfirm,
  onSubmitForm
}) => {
  const { t, i18n } = useTranslation();
  const prevDeclarationNameRef = useRef('');

  useEffect(
    () => {
      if (!formCode) return;

      let declarationName = '';

      if (year) {
        if (periodType === 'monthly' && periodMonth) {
          moment.locale(i18n.language);
          const monthName = moment(periodMonth, 'M').format('MMMM');
          declarationName = t('features.ReportIncomeTax.create.declaration_name_monthly', {
            formCode,
            month: monthName,
            year
          });
        } else if (periodType === 'quarterly' && periodQuarter) {
          declarationName = t('features.ReportIncomeTax.create.declaration_name_quarterly', {
            formCode,
            quarter: periodQuarter,
            year
          });
        }
      }

      if (declarationName && declarationName !== prevDeclarationNameRef.current) {
        prevDeclarationNameRef.current = declarationName;
        onChangeDeclarationName(declarationName);
      }
    },
    [periodType, year, periodMonth, periodQuarter, formCode, onChangeDeclarationName, t, i18n.language]
  );

  return (
    <div>
      <div className="l-title-wrap">
        <h1 className="m-title-main">{t('features.ReportIncomeTax.create.title')}</h1>
        <p className="m-title-main-note">{t('features.ReportIncomeTax.create.subtitle')}</p>
      </div>
      <div className="l-wrap-xs create-declaration-form">
        <div className="u-mb20">
          <BreadCrumb>
            <BreadCrumb.Item label={t('features.Reports.Report.create_document')} path={REPORT_INDEX} />
            <BreadCrumb.Item label={t('features.ReportIncomeTax.title')} path={INCOME_TAX_DECLARATION_LIST} />
            <BreadCrumb.Item label={t('features.ReportIncomeTax.create.breadcrumb')} />
          </BreadCrumb>
        </div>

        <form onSubmit={handleSubmit(onSubmitForm)}>
          {error && (
            <div className="form-error-message">
              <p>{error}</p>
            </div>
          )}
          {formErrors && formErrors.length > 0 && <ErrorsSection errors={formErrors} />}
          <Section title={t('features.ReportIncomeTax.create.general_information')}>
            <div className={formStyles.boxWrap}>
              <Label text={t('features.ReportIncomeTax.create.declaration_form')} />
              <div className="readonly-field">{t('features.ReportIncomeTax.create.form_code_label')}</div>
            </div>

            <div className="branch-field-group">
              <SelectField name="branchId" label={t('features.ReportIncomeTax.create.branch')} options={branchOptions} required />

              {selectedBranch &&
                selectedBranch.dependentBranches &&
                selectedBranch.dependentBranches.length > 0 && (
                  <div className="dependent-branches-info">
                    <p className="dependent-branches-title">{t('features.ReportIncomeTax.create.dependent_branches_title')}</p>
                    <ul className="dependent-branches-list">{selectedBranch.dependentBranches.map(branch => <li key={branch.id}>{branch.name}</li>)}</ul>
                  </div>
                )}
            </div>

            <Field name="year" component={YearPickerField} label={t('features.ReportIncomeTax.create.year')} required defaultToCurrentYear />

            <div className={formStyles.boxWrap}>
              <Label text={t('features.ReportIncomeTax.create.period')} required />
              <div className="period-controls">
                <div className="period-radio">
                  <RadioField name="periodType" options={periodTypes} />
                </div>
                <div className="period-select">
                  {periodType === 'monthly' ? (
                    <SelectField name="periodMonth" options={getMonthOptions(i18n.language)} noLabel addBlankOption={false} />
                  ) : (
                    <SelectField name="periodQuarter" options={getQuarterOptions(t)} noLabel addBlankOption={false} />
                  )}
                </div>
              </div>
            </div>

            <TextField name="declarationName" label={t('features.ReportIncomeTax.create.declaration_name')} required />

            {canIncludeAppendix && (
              <div className="appendix-section">
                <CheckboxField
                  name="includeAppendix051"
                  label={t('features.ReportIncomeTax.create.appendix_051.label')}
                  description={t('features.ReportIncomeTax.create.appendix_051.description')}
                />
              </div>
            )}
          </Section>

          <div className="action-buttons">
            <Button as="button" type="button" onClick={onCancel}>
              {t('features.ReportIncomeTax.create.cancel')}
            </Button>
            <Button as="button" type="submit" primary disabled={submitting}>
              {t('features.ReportIncomeTax.create.create')}
            </Button>
          </div>
        </form>
      </div>
      <CreateDeclarationLoadingModal
        isOpen={isCreating}
        description={t('features.ReportIncomeTax.create.loading_description', { declarationName: prevDeclarationNameRef.current })}
      />
      <ExistingDeclarationModal
        isOpen={isExistingModalOpen}
        periodType={periodType}
        year={year}
        periodMonth={periodMonth}
        periodQuarter={periodQuarter}
        onCancel={onExistingModalCancel}
        onConfirm={onExistingModalConfirm}
      />
      <UnconfirmedPayrollModal
        isOpen={isUnconfirmedModalOpen}
        periodType={periodType}
        year={year}
        periodMonth={periodMonth}
        periodQuarter={periodQuarter}
        unconfirmedPaymentMonths={unconfirmedPaymentMonths}
        onCancel={onUnconfirmedModalCancel}
        onConfirm={onUnconfirmedModalConfirm}
      />
    </div>
  );
};

CreateDeclarationForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
  periodType: PropTypes.string,
  year: PropTypes.number,
  periodMonth: PropTypes.number,
  periodQuarter: PropTypes.number,
  formCode: PropTypes.string,
  branchOptions: PropTypes.array,
  selectedBranch: PropTypes.shape({
    value: PropTypes.number,
    label: PropTypes.string,
    dependentBranches: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string
      })
    )
  }),
  periodTypes: PropTypes.array,
  canIncludeAppendix: PropTypes.bool,
  isCreating: PropTypes.bool,
  formErrors: PropTypes.arrayOf(PropTypes.string),
  isExistingModalOpen: PropTypes.bool,
  isUnconfirmedModalOpen: PropTypes.bool,
  unconfirmedPaymentMonths: PropTypes.arrayOf(
    PropTypes.shape({
      paymentDate: PropTypes.string
    })
  ),
  onCancel: PropTypes.func.isRequired,
  onChangeDeclarationName: PropTypes.func.isRequired,
  onExistingModalCancel: PropTypes.func.isRequired,
  onExistingModalConfirm: PropTypes.func.isRequired,
  onUnconfirmedModalCancel: PropTypes.func.isRequired,
  onUnconfirmedModalConfirm: PropTypes.func.isRequired,
  onSubmitForm: PropTypes.func.isRequired
};

CreateDeclarationForm.defaultProps = {
  submitting: false,
  periodType: 'monthly',
  year: moment().year(),
  periodMonth: 12,
  periodQuarter: 1,
  formCode: '',
  branchOptions: [],
  selectedBranch: null,
  periodTypes: [],
  canIncludeAppendix: false,
  isCreating: false,
  formErrors: [],
  isExistingModalOpen: false,
  isUnconfirmedModalOpen: false,
  unconfirmedPaymentMonths: []
};

export default CreateDeclarationForm;
